import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShowCard } from "@/components/ShowCard";
import { fmtDate, relFromNow } from "@/lib/date";
import {
  Drama, Sparkles, UserRound, CalendarClock, Users, Heart, Mail, Bookmark, BookOpen,
  Trash2, Star, Plus, X, Pencil, Flame, Check, Globe, Lock, Copy, Share2, Ticket, MapPin, ThumbsUp, ListOrdered, MessageCircle, ImagePlus,
} from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import FriendsTab from "@/pages/FriendsTab";
import ProfileBar from "@/components/ProfileBar";
import RequestsBanner from "@/components/RequestsBanner";
import { useDialog } from "@/components/Dialogs";
import DiaryExport from "@/components/DiaryExport";
import ListsNotesTab from "@/pages/ListsNotesTab";
import FeedTab from "@/pages/FeedTab";
import MessagesTab from "@/pages/MessagesTab";
import { Comments } from "@/components/ContentBits";
import { PostImages } from "@/components/Posts";
import { uploadPhoto } from "@/components/photoUpload";
import { DiaryPoster, dateParts, showAccent, ratingLabel, groupByMonth, computeHighlights, DiaryHeader, MonthRibbon, Highlights, EntryExtras, TAGS } from "@/components/DiaryPoster";

const HERO = "https://images.unsplash.com/photo-1646186213674-35cf3c0c398d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const TAB_KEY = "mytheatre-tab";
const ENTRY_PHOTOS = 4;
const todayStr = () => new Date().toISOString().slice(0, 10);

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function Stars({ value, onChange, size = "h-6 w-6" }) {
  return (
    <div className="inline-flex gap-1" role={onChange ? "radiogroup" : undefined} aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const on = value >= n;
        const icon = <Star className={`${size} ${on ? "fill-[#C89B3C] text-[#C89B3C]" : "text-muted-foreground/40"}`} />;
        return onChange ? (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            onClick={() => onChange(value === n ? null : n)}
          >
            {icon}
          </button>
        ) : (
          <span key={n}>{icon}</span>
        );
      })}
    </div>
  );
}

function Hint({ children }) {
  return <p className="rounded-xl border border-dashed border-border bg-card p-5 text-sm text-muted-foreground">{children}</p>;
}

function ActivityIcon({ kind }) {
  if (kind === "booking") return <CalendarClock className="h-4 w-4 text-[#63B3ED]" />;
  return <Users className="h-4 w-4 text-[#FF758F]" />;
}

function SectionTitle({ icon: Icon, tone, count, children }) {
  return (
    <h2 className="flex items-center gap-2.5 font-serif text-2xl font-semibold">
      <span className={`flex h-8 w-8 items-center justify-center rounded-full ${tone}`}><Icon className="h-4 w-4" /></span>
      {children}
      {count > 0 && <span className="text-base font-normal text-muted-foreground">({count})</span>}
    </h2>
  );
}

function SignInPrompt() {
  return (
    <section className="relative overflow-hidden hero-scrim grain" style={{ "--hero-img": `url(${HERO})` }}>
      <Seo title="My Theatre" noindex />
      <div className="relative z-10 mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto max-w-lg rounded-2xl border border-amber-900/20 bg-[#0D0A0E]/60 p-8 text-center backdrop-blur-sm sm:p-10">
          <BookOpen className="mx-auto h-8 w-8 text-[#C89B3C]" />
          <h1 className="mt-4 font-serif text-2xl font-semibold text-amber-50 sm:text-3xl">Your own theatre scrapbook</h1>
          <p className="mt-3 text-amber-100/70">
            Create a free account to keep a watchlist, log and rate every show you see, and follow shows and actors. Add friends to see what they have been to, share posts and lists, and message each other. We will email you the moment something changes.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/login" className="rounded-full bg-[#9E1B28] px-6 py-2.5 text-sm font-medium text-amber-50 hover:bg-[#B22232]">Sign in</Link>
            <Link to="/login" className="rounded-full bg-white/5 px-6 py-2.5 text-sm font-medium text-amber-100/80 hover:bg-white/10">Create an account</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- diary form ---------- */
function EntryForm({ show, entry, seatAreas, profile, onClose, onSaved }) {
  // show: {id,name,venue}. entry: existing diary entry when editing.
  const [f, setF] = useState(() => ({
    date: entry?.date || todayStr(),
    rating: entry?.rating ?? null,
    note: entry?.note || "",
    seat_area: entry?.seat_area || "",
    went_with: entry?.went_with || "",
    favourite_moment: entry?.favourite_moment || "",
    would_see_again: entry?.would_see_again ?? null,
    verdict: entry?.verdict || "",
    review: entry?.review || "",
    best_performance: entry?.best_performance || "",
    recommend_for: entry?.recommend_for || "",
    tags: entry?.tags || [],
    visibility: entry ? (entry.visibility || (entry.is_public ? "public" : "private")) : (profile?.profile_public ? "public" : "private"),
    share_note: entry ? Boolean(entry.share_note) : true,   // new entries start with "Include my thoughts" ticked (it only matters once the entry is shared); editing keeps what was chosen
  }));
  const custom = Boolean(show.custom || entry?.is_custom);
  const [name, setName] = useState(show.name || "");
  const [venue, setVenue] = useState(show.venue || "");
  const [cast, setCast] = useState([]);
  const [picked, setPicked] = useState(() => new Set((entry?.cast_seen || []).map((c) => c.actor_name)));
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState(entry?.images || []);
  const [uploading, setUploading] = useState(0);
  const fresh = useRef(new Set());      // photos uploaded in this box and not yet saved
  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = ENTRY_PHOTOS - photos.length - uploading;
    if (files.length > room) toast.error(room > 0 ? `You can add ${room} more photo${room === 1 ? "" : "s"} to this entry.` : `An entry can have up to ${ENTRY_PHOTOS} photos.`);
    for (const file of files.slice(0, Math.max(room, 0))) {
      setUploading((n) => n + 1);
      try {
        const v = await uploadPhoto(file, "diary");
        fresh.current.add(v.id);
        setPhotos((a) => [...a, v]);
      } catch (err) { toast.error(errText(err, "Could not upload that photo. Please try another.")); }
      finally { setUploading((n) => n - 1); }
    }
  };
  const removePhoto = (p) => {
    setPhotos((a) => a.filter((x) => x.id !== p.id));
    if (fresh.current.delete(p.id)) api.delete(`/media/${p.id}`).catch(() => {});   // never saved, so tidy it away now
  };
  const close = () => { fresh.current.forEach((id) => api.delete(`/media/${id}`).catch(() => {})); onClose(); };
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const [showReview, setShowReview] = useState(Boolean(entry?.review));
  const toggleTag = (t) => setF((x) => ({ ...x, tags: x.tags.includes(t) ? x.tags.filter((y) => y !== t) : x.tags.length >= 6 ? x.tags : [...x.tags, t] }));

  useEffect(() => {
    if (custom) return;
    api.get(`/diary/cast/${show.id}`).then((r) => {
      const have = new Set(r.data.map((c) => c.actor_name));
      const extra = (entry?.cast_seen || []).filter((c) => !have.has(c.actor_name));
      setCast([...r.data, ...extra]);
    }).catch(() => setCast(entry?.cast_seen || []));
  }, [show.id]); // eslint-disable-line

  const toggle = (name) =>
    setPicked((p) => { const n = new Set(p); n.has(name) ? n.delete(name) : n.add(name); return n; });

  const save = async (e) => {
    e.preventDefault();
    if (uploading > 0) return;
    setBusy(true);
    const body = { ...f, cast_seen: cast.filter((c) => picked.has(c.actor_name)), media_ids: photos.map((p) => p.id) };
    if (custom) { body.custom_name = name; body.custom_venue = venue; }
    try {
      const r = entry
        ? await api.put(`/diary/${entry.id}`, body)
        : await api.post("/diary", custom ? body : { ...body, show_id: show.id });
      toast.success(entry ? "Diary entry updated" : "Added to your diary");
      fresh.current.clear();
      onSaved(r.data);
    } catch (err) {
      toast.error(errText(err, "Could not save that. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const input = "mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Diary entry">
      <form onSubmit={save} className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-border bg-card p-5 sm:rounded-2xl sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="overline-label text-[#C89B3C]">{entry ? "Edit entry" : "Add to your diary"}</p>
            {custom ? <h2 className="font-serif text-2xl font-semibold">A show that's not listed</h2> : <h2 className="font-serif text-2xl font-semibold">{show.name}</h2>}
            {!custom && show.venue && <p className="text-sm text-muted-foreground">{show.venue}</p>}
          </div>
          <button type="button" onClick={close} aria-label="Close" className="rounded-full p-1.5 hover:bg-secondary"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-5 space-y-4">
          {custom && (
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium">Show name
                <input required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} className={input} placeholder="e.g. Six" data-testid="custom-name" />
              </label>
              <label className="block text-sm font-medium">Theatre <span className="font-normal text-muted-foreground">(optional)</span>
                <input maxLength={80} value={venue} onChange={(e) => setVenue(e.target.value)} className={input} placeholder="e.g. Vaudeville Theatre" />
              </label>
            </div>
          )}
          <label className="block text-sm font-medium">Date you went
            <input type="date" required max={todayStr()} value={f.date} onChange={(e) => set("date", e.target.value)} className={input} />
          </label>
          <div className="text-sm font-medium">Your rating
            <div className="mt-1"><Stars value={f.rating || 0} onChange={(v) => set("rating", v)} /></div>
          </div>
          <label className="block text-sm font-medium">Your thoughts <span className="font-normal text-muted-foreground">({f.note.length}/500)</span>
            <textarea rows={3} maxLength={500} value={f.note} onChange={(e) => set("note", e.target.value)} className={input} placeholder="What did you think?" />
          </label>
          <label className="block text-sm font-medium">One-line verdict <span className="font-normal text-muted-foreground">(optional)</span>
            <input maxLength={120} value={f.verdict} onChange={(e) => set("verdict", e.target.value)} className={input} placeholder="Sum it up in a sentence" data-testid="verdict-input" />
          </label>
          <div className="text-sm font-medium">Tags <span className="font-normal text-muted-foreground">(up to 6)</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {TAGS.map((t) => {
                const on = f.tags.includes(t);
                return (
                  <button key={t} type="button" onClick={() => toggleTag(t)} aria-pressed={on} data-testid={`tag-${t}`}
                    className={`rounded-full border px-3 py-1 text-xs font-normal transition-colors ${on ? "border-[#C89B3C] bg-[#C89B3C]/20 text-foreground" : "border-border hover:border-[#C89B3C]/60"}`}>{t}</button>
                );
              })}
            </div>
          </div>
          {showReview ? (
            <label className="block text-sm font-medium">Your review <span className="font-normal text-muted-foreground">({f.review.length}/3000)</span>
              <textarea rows={7} maxLength={3000} value={f.review} onChange={(e) => set("review", e.target.value)} className={input} placeholder="Write as much as you like" data-testid="review-input" />
            </label>
          ) : (
            <button type="button" onClick={() => setShowReview(true)} data-testid="write-review" className="text-sm font-medium text-[#C89B3C] hover:underline">+ Write a longer review</button>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium">Best performance
              <input maxLength={120} value={f.best_performance} onChange={(e) => set("best_performance", e.target.value)} className={input} placeholder="Who stole the show?" data-testid="best-performance" />
            </label>
            <label className="block text-sm font-medium">Who should see this?
              <input maxLength={120} value={f.recommend_for} onChange={(e) => set("recommend_for", e.target.value)} className={input} placeholder="e.g. anyone who loves a big finale" />
            </label>
          </div>
          <label className="block text-sm font-medium">Favourite moment
            <input maxLength={120} value={f.favourite_moment} onChange={(e) => set("favourite_moment", e.target.value)} className={input} placeholder="The bit you'll remember" />
          </label>
          {cast.length > 0 && (
            <div className="text-sm font-medium">Cast I saw
              <div className="mt-2 flex flex-wrap gap-2">
                {cast.map((c) => {
                  const on = picked.has(c.actor_name);
                  return (
                    <button key={c.actor_name} type="button" onClick={() => toggle(c.actor_name)} aria-pressed={on}
                      className={`rounded-full border px-3 py-1 text-xs font-normal transition-colors ${on ? "border-[#C89B3C] bg-[#C89B3C]/20 text-foreground" : "border-border hover:border-[#C89B3C]/60"}`}>
                      {on && <Check className="mr-1 inline h-3 w-3" />}{c.actor_name}{c.role ? ` · ${c.role}` : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium">Where you sat
              <select value={f.seat_area} onChange={(e) => set("seat_area", e.target.value)} className={input}>
                <option value="">Not sure / skip</option>
                {seatAreas.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block text-sm font-medium">Who with
              <input maxLength={60} value={f.went_with} onChange={(e) => set("went_with", e.target.value)} className={input} placeholder="Mum, Sam…" />
            </label>
          </div>
          <div className="text-sm font-medium">Would you see it again?
            <div className="mt-1 flex gap-2">
              {[[true, "Yes"], [false, "No"]].map(([v, label]) => (
                <button key={label} type="button" aria-pressed={f.would_see_again === v}
                  onClick={() => set("would_see_again", f.would_see_again === v ? null : v)}
                  className={`rounded-full border px-4 py-1.5 text-sm font-normal ${f.would_see_again === v ? "border-[#C89B3C] bg-[#C89B3C]/20" : "border-border"}`}>{label}</button>
              ))}
            </div>
          </div>

          <div className="text-sm font-medium" data-testid="diary-photos">Photos <span className="font-normal text-muted-foreground">(optional, up to {ENTRY_PHOTOS})</span>
            {(photos.length > 0 || uploading > 0) && (
              <div className="mt-2 grid grid-cols-4 gap-2">
                {photos.map((p) => (
                  <div key={p.id} className="relative aspect-square overflow-hidden rounded-lg border border-border" data-testid="diary-photo-thumb">
                    <img src={p.thumb || p.url} alt="Attached photo" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => removePhoto(p)} aria-label="Remove photo" data-testid="diary-photo-remove" className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {Array.from({ length: uploading }).map((_, i) => <div key={`u${i}`} className="aspect-square animate-pulse rounded-lg bg-secondary" data-testid="diary-photo-uploading" />)}
              </div>
            )}
            {photos.length + uploading < ENTRY_PHOTOS && (
              <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 font-medium text-[#C89B3C] underline">
                <ImagePlus className="h-4 w-4" aria-hidden="true" /> Add photos
                <input type="file" accept="image/*" multiple className="sr-only" data-testid="diary-photo-input" onChange={pickPhotos} />
              </label>
            )}
            {(photos.length > 0 || uploading > 0) && (
              <p className="mt-2 text-xs font-normal text-muted-foreground" data-testid="diary-photo-note">
                {f.visibility === "private" ? "Only you can see this entry and its photos." : "Your photos are shown to your accepted friends only. They never appear on your public page or to other visitors."}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border p-3 text-sm" data-testid="visibility-choice">
            <p className="font-medium">Who can see this entry?</p>
            <div className="mt-2 space-y-2">
              {[
                ["private", "Only me", "Nobody else can see it."],
                ["friends", "My friends", "Friends you've accepted can see the show, theatre, date and rating."],
                ["public", "Anyone with my link", profile?.username ? "Shown on your public page, once it's switched on." : "You'll need to set up your public page first (Diary tab). Until then this stays private."],
              ].map(([value, label, hint]) => (
                <label key={value} className="flex items-start gap-2">
                  <input type="radio" name="visibility" value={value} checked={f.visibility === value} onChange={() => set("visibility", value)} className="mt-1" data-testid={`vis-${value}`} />
                  <span className="font-medium">{label}<span className="block text-xs font-normal text-muted-foreground">{hint}</span></span>
                </label>
              ))}
            </div>
            {f.visibility !== "private" && (
              <label className="mt-3 flex items-start gap-2 border-t border-border pt-3 font-medium">
                <input type="checkbox" checked={f.share_note} onChange={(e) => set("share_note", e.target.checked)} className="mt-1" data-testid="share-note" />
                <span>Include my thoughts <span className="block text-xs font-normal text-muted-foreground">Includes your thoughts, verdict, tags, review and best performance. Who you went with, seat, cast and favourite moment always stay private.</span></span>
              </label>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={close} className="rounded-full px-5 py-2 text-sm text-muted-foreground hover:text-foreground">Cancel</button>
          <button type="submit" disabled={busy || uploading > 0} data-testid="entry-save" className="rounded-full bg-[#C89B3C] px-6 py-2 text-sm font-semibold text-black disabled:opacity-60">
            {busy ? "Saving…" : entry ? "Save changes" : "Add to diary"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- pick any show to log ---------- */
function ShowPicker({ onPick, onClose }) {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/shows").then((r) => setAll(Array.isArray(r.data) ? r.data : r.data?.shows || [])).catch(() => setAll([])); }, []);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (all || []).filter((x) => !s || (x.name || "").toLowerCase().includes(s)).slice(0, 300);
  }, [all, q]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Choose a show">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-t-2xl border border-border bg-card p-5 sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold">Which show did you see?</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1.5 hover:bg-secondary"><X className="h-5 w-5" /></button>
        </div>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shows" className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
        <button type="button" data-testid="show-not-listed" onClick={() => onPick({ custom: true })}
          className="mt-3 w-full rounded-lg border border-dashed border-[#C89B3C]/60 px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-[#C89B3C]/15">
          <Plus className="mr-1 inline h-4 w-4" /> My show isn't listed. Add it myself
        </button>
        <ul className="mt-3 flex-1 overflow-y-auto">
          {!all && <li className="p-3 text-sm text-muted-foreground">Loading…</li>}
          {all && hits.length === 0 && <li className="p-3 text-sm text-muted-foreground">No shows match. Use "My show isn't listed" above to add it yourself.</li>}
          {hits.map((s) => (
            <li key={s.id}>
              <button type="button" onClick={() => onPick(s)} className="w-full rounded-lg px-3 py-2 text-left hover:bg-secondary">
                <span className="font-medium">{s.name}</span>
                {s.venue && <span className="block text-xs text-muted-foreground">{s.venue}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------- sharing panel ---------- */
function SharingPanel({ profile, onChange, entries, needsCount, onBulk }) {
  const dialog = useDialog();
  const [name, setName] = useState(profile.username || "");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(!profile.username);
  const [open, setOpen] = useState(!profile.profile_public);
  const link = profile.username ? `${window.location.origin}/u/${profile.username}` : "";

  const save = async (username, pub) => {
    setBusy(true);
    try {
      const r = await api.put("/diary-profile", { username, public: pub });
      onChange(r.data); setName(r.data.username); setEditing(false);
      toast.success(pub ? "Your public page is on" : "Saved");
    } catch (err) { toast.error(errText(err, "Could not save that. Please try again.")); }
    finally { setBusy(false); }
  };
  const total = entries.length;
  const level = (e) => e.visibility || (e.is_public ? "public" : "private");
  const pub = entries.filter((e) => level(e) === "public").length;
  const fr = entries.filter((e) => level(e) === "friends").length;
  const priv = total - pub - fr;
  const bulk = async (to) => {
    const msg = {
      public: `Make all ${total} diary entries visible to anyone with your link? Only the show, theatre, date and rating are shown. Your thoughts stay private unless you've ticked them on an entry.`,
      friends: `Make all ${total} diary entries visible to your friends?`,
    }[to];
    if (msg && !(await dialog.confirm({ title: "Change every diary entry?", body: msg, confirmLabel: "Yes, change them all" }))) return;
    setBusy(true);
    try {
      await api.put("/diary-visibility", { visibility: to });
      toast.success({ public: "All entries are now public", friends: "All entries are now friends-only", private: "All entries are now private" }[to]);
      await onBulk();
    } catch { toast.error("Could not change that. Please try again."); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch { toast.error("Could not copy. Select the link and copy it."); }
  };
  const share = async () => {
    try { await navigator.share({ title: "My Theatre diary", url: link }); } catch { /* cancelled */ }
  };

  return (
    <div className="mb-6 rounded-2xl border border-border bg-card p-5" data-testid="sharing-panel">
      {dialog.node}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="sharing-toggle"
          className="flex items-center gap-3 text-left">
          <Globe className="h-5 w-5 text-[#C89B3C]" />
          <span className="font-medium text-foreground">Share your diary</span>
          {profile.profile_public && <span className="rounded-full bg-[#C89B3C]/20 px-2.5 py-0.5 text-xs text-[hsl(var(--gold))]">Public page on</span>}
        </button>
        <span className="flex items-center gap-4 text-xs">
          {profile.username && profile.profile_public && (
            <a href={link} target="_blank" rel="noreferrer" data-testid="view-my-page" className="font-medium text-[#C89B3C] hover:underline">View my page</a>
          )}
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="sharing-manage" className="text-muted-foreground hover:text-foreground">{open ? "Hide" : "Manage"}</button>
        </span>
      </div>
      {open && <div className="mt-4 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-muted-foreground">
            Optional. Everything is private unless you choose to share it with friends or with anyone who has your link.
          </p>
          {profile.blocked && <p className="mt-2 text-sm text-red-400">Your public page has been switched off. Contact us if you think that's a mistake.</p>}
          {!profile.blocked && editing && (
            <form className="mt-3 flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); save(name, profile.profile_public); }}>
              <span className="text-sm text-muted-foreground">mytheatre.uk/u/</span>
              <input value={name} onChange={(e) => setName(e.target.value.toLowerCase())} maxLength={20} placeholder="username" aria-label="Username"
                data-testid="username-input" className="w-40 rounded-lg border border-border bg-background px-3 py-1.5 text-sm" />
              <button type="submit" disabled={busy || name.trim().length < 3} data-testid="save-username"
                className="rounded-full bg-[#C89B3C] px-4 py-1.5 text-sm font-semibold text-black disabled:opacity-60">{profile.username ? "Save" : "Choose username"}</button>
              {profile.username && <button type="button" onClick={() => { setEditing(false); setName(profile.username); }} className="text-sm text-muted-foreground">Cancel</button>}
            </form>
          )}
          {!profile.blocked && !editing && (
            <div className="mt-3 space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={profile.profile_public} disabled={busy} data-testid="public-toggle"
                  onChange={(e) => save(profile.username, e.target.checked)} />
                Public page is {profile.profile_public ? "on" : "off"}
              </label>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="break-all text-muted-foreground">{link}</span>
                <button type="button" onClick={() => setEditing(true)} className="text-xs text-muted-foreground underline">Change username</button>
              </div>
              <p className="text-sm" data-testid="public-count">
                <strong>{pub}</strong> public · <strong>{fr}</strong> friends only · <strong>{priv}</strong> private
                {profile.profile_public && pub === 0 && total > 0 && <span className="text-[#C89B3C]"> Nothing is on your public page yet. Change an entry to "Anyone with my link", or use the buttons below.</span>}
              </p>
              {needsCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {needsCount} show{needsCount > 1 ? "s" : ""} you marked as seen {needsCount > 1 ? "aren't" : "isn't"} in your diary yet, so {needsCount > 1 ? "they" : "it"} won't appear on your page. Add details below to include {needsCount > 1 ? "them" : "it"}.
                </p>
              )}
              {total > 0 && (
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={busy || fr === total} onClick={() => bulk("friends")} data-testid="make-all-friends"
                    className="rounded-full border border-border px-4 py-1.5 text-sm hover:border-[#C89B3C] disabled:opacity-50">Make all friends only</button>
                  <button type="button" disabled={busy || pub === total} onClick={() => bulk("public")} data-testid="make-all-public"
                    className="rounded-full border border-border px-4 py-1.5 text-sm hover:border-[#C89B3C] disabled:opacity-50">Make all public</button>
                  <button type="button" disabled={busy || priv === total} onClick={() => bulk("private")} data-testid="make-all-private"
                    className="rounded-full border border-border px-4 py-1.5 text-sm hover:border-[#C89B3C] disabled:opacity-50">Make all private</button>
                </div>
              )}
              {profile.profile_public && (
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={copy} data-testid="copy-link" className="inline-flex items-center gap-1.5 rounded-full border border-[#C89B3C]/60 px-4 py-1.5 text-sm hover:bg-[#C89B3C]/15"><Copy className="h-4 w-4" /> Copy link</button>
                  {typeof navigator !== "undefined" && navigator.share && (
                    <button type="button" onClick={share} className="inline-flex items-center gap-1.5 rounded-full border border-[#C89B3C]/60 px-4 py-1.5 text-sm hover:bg-[#C89B3C]/15"><Share2 className="h-4 w-4" /> Share</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}

/* Comments friends have left on one of your shared diary entries (a private entry can't have any). */
function EntryComments({ entry }) {
  const [n, setN] = useState(entry.comment_count || 0);
  const level = entry.visibility || (entry.is_public ? "public" : "private");
  if (level === "private") return null;
  return <Comments kind="entry" id={entry.id} count={n} onCount={setN} />;
}

/* ---------- diary tab ---------- */
function DiaryTab({ diary, onAdd, onEdit, onDelete, onProfile, onBulk, onSuggest, onAgain }) {
  const [year, setYear] = useState("all");
  const entries = diary.entries;
  const years = useMemo(() => [...new Set(entries.map((e) => e.date.slice(0, 4)))].sort().reverse(), [entries]);
  const shown = year === "all" ? entries : entries.filter((e) => e.date.startsWith(year));
  // visits to the same show, oldest first, so each card can say "2nd visit" and what you thought last time
  const visits = useMemo(() => {
    const byShow = {};
    for (const e of entries) (byShow[e.show_id] = byShow[e.show_id] || []).push(e);
    const info = {};
    for (const list of Object.values(byShow)) {
      const asc = [...list].sort((a, b) => a.date.localeCompare(b.date) || (a.created_at || "").localeCompare(b.created_at || ""));
      asc.forEach((e, i) => { info[e.id] = { n: i + 1, total: asc.length, prev: i > 0 ? asc[i - 1] : null }; });
    }
    return info;
  }, [entries]);
  const st = diary.stats;

  return (
    <div>
      <DiaryHeader title={diary.profile?.username ? `${diary.profile.username}'s Theatre Diary` : "My Theatre Diary"} stats={st} />
      {entries.length > 1 && <div className="mt-4"><Highlights h={computeHighlights(entries, new Date().getFullYear())} /></div>}
      <div className="mt-6">
      <SharingPanel key={diary.profile?.username || "new"} profile={diary.profile} onChange={onProfile} entries={entries} needsCount={diary.needs_details.length} onBulk={onBulk} />
      </div>

      {diary.needs_details.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[#C89B3C]/25 bg-card p-5" data-testid="needs-details">
          <p className="font-medium text-foreground">You've marked {diary.needs_details.length} show{diary.needs_details.length > 1 ? "s" : ""} as seen</p>
          <p className="text-sm text-muted-foreground">Add the date and your thoughts to bring them into your diary.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {diary.needs_details.map((s) => (
              <button key={s.id} type="button" onClick={() => onAdd(s)}
                className="rounded-full border border-[#C89B3C]/50 px-4 py-1.5 text-sm hover:bg-[#C89B3C]/15">
                <Plus className="mr-1 inline h-3.5 w-3.5" />{s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        {years.length > 1 ? (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by year">
            {["all", ...years].map((y) => (
              <button key={y} type="button" onClick={() => setYear(y)} aria-pressed={year === y}
                className={`rounded-full border px-3.5 py-1 text-sm ${year === y ? "border-[#C89B3C] bg-[#C89B3C]/20" : "border-border text-muted-foreground"}`}>
                {y === "all" ? "All" : y}
              </button>
            ))}
          </div>
        ) : <span />}
        <div className="flex flex-wrap items-center gap-2">
          <DiaryExport entries={entries} username={diary.profile?.username} defaultYear={year} />
          <button type="button" onClick={() => onAdd(null)} data-testid="add-diary"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B3C] px-5 py-2 text-sm font-semibold text-black">
            <Plus className="h-4 w-4" /> Add a show
          </button>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="mt-6 overflow-hidden rounded-2xl border border-[#C89B3C]/30 bg-gradient-to-br from-[#2a1620] to-[#0D0A0E] p-10 text-center">
          <BookOpen className="mx-auto h-10 w-10 text-[#C89B3C]" />
          <h3 className="mt-3 font-serif text-2xl font-semibold text-amber-50">Your diary is empty</h3>
          <p className="mx-auto mt-2 max-w-sm text-sm text-amber-100/70">Log the shows you've seen with a date, a rating and your thoughts. It becomes your own theatre scrapbook.</p>
          <button type="button" onClick={() => onAdd(null)} className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black"><Plus className="h-4 w-4" /> Log your first show</button>
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          {groupByMonth(shown).map((g) => (
            <section key={g.key}>
              <MonthRibbon label={g.label} count={g.items.length} />
              <ul className="mt-3 space-y-4">
          {g.items.map((e) => (
            <li key={e.id} data-testid={`entry-${e.id}`} className="group overflow-hidden rounded-2xl border border-border bg-card transition-colors hover:border-[#C89B3C]/50" style={{ borderLeft: `4px solid ${showAccent(e.show_name)}` }}>
              <div className="flex">
                <div className="w-[5rem] flex-shrink-0 self-stretch bg-secondary sm:w-[6rem]" style={{ minHeight: "8.5rem" }}>
                  <DiaryPoster src={e.image_url} name={e.show_name} />
                </div>
                <div className="min-w-0 flex-1 p-4">
                  {e.is_custom ? <span className="font-serif text-xl font-semibold leading-tight">{e.show_name}</span> : <Link to={`/shows/${e.show_slug || e.show_id}`} className="font-serif text-xl font-semibold leading-tight hover:text-primary">{e.show_name}</Link>}
                  {(e.venue || e.seat_area || e.went_with) && (
                    <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-sm text-muted-foreground">
                      {e.venue && <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-[#C89B3C]" />}
                      {[e.venue, e.seat_area, e.went_with && `with ${e.went_with}`].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  {e.rating ? <div className="mt-2 flex items-center gap-2"><Stars value={e.rating} size="h-5 w-5" /><span className="text-xs font-medium text-[hsl(var(--gold))]">{ratingLabel(e.rating)}</span></div> : null}
                  {visits[e.id]?.total > 1 && (
                    <p className="mt-2 text-xs text-muted-foreground" data-testid="visit-info">
                      <span className="rounded-full bg-[#C89B3C]/15 px-2 py-0.5 font-semibold text-[hsl(var(--gold))]">Visit {visits[e.id].n} of {visits[e.id].total}</span>
                      {visits[e.id].prev?.rating ? <span className="ml-2">Last time: {"★".repeat(visits[e.id].prev.rating)} ({fmtDate(visits[e.id].prev.date)}){e.rating && e.rating !== visits[e.id].prev.rating ? (e.rating > visits[e.id].prev.rating ? " · you liked it more this time" : " · you liked it less this time") : ""}</span> : null}
                    </p>
                  )}
                  <EntryExtras e={e} />
                  <PostImages images={e.images} who="" what="diary entry" />
                  {e.favourite_moment && <p className="mt-2 border-l-2 border-[#C89B3C]/60 pl-3 text-sm italic text-[hsl(var(--gold))]">✨ {e.favourite_moment}</p>}
                  {e.cast_seen?.length > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">Cast: {e.cast_seen.map((c) => c.role ? `${c.actor_name} (${c.role})` : c.actor_name).join(", ")}</p>
                  )}
                  {e.would_see_again !== null && e.would_see_again !== undefined && (
                    <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground"><ThumbsUp className="h-3 w-3" /> {e.would_see_again ? "Would see again" : "Wouldn't see again"}</p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <button type="button" onClick={() => onEdit(e)} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                    <button type="button" onClick={() => onAgain(e)} data-testid="log-again" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><Plus className="h-3.5 w-3.5" /> Log another visit</button>
                    <button type="button" onClick={() => onDelete(e)} className="inline-flex items-center gap-1 text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                    {e.is_custom && (e.suggested
                      ? <span className="inline-flex items-center gap-1 text-[#C89B3C]" data-testid="suggested"><Check className="h-3.5 w-3.5" /> Suggested to us</span>
                      : <button type="button" onClick={() => onSuggest(e)} data-testid="suggest-show" className="inline-flex items-center gap-1 text-[#C89B3C] hover:underline"><Plus className="h-3.5 w-3.5" /> Suggest this show be added</button>)}
                  </div>
                  <EntryComments entry={e} />
                </div>
                <div className="flex w-[4.5rem] flex-shrink-0 flex-col items-center justify-center gap-0.5 border-l border-dashed border-[#C89B3C]/40 bg-foreground/5 px-2 text-center sm:w-[5.5rem]" data-testid="date-badge">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-[#C89B3C]">{dateParts(e.date).month}</span>
                  <span className="font-serif text-3xl font-bold leading-none text-foreground">{dateParts(e.date).day}</span>
                  <span className="text-xs text-muted-foreground">{dateParts(e.date).year}</span>
                  <span className="mt-1">{(e.visibility || (e.is_public ? "public" : "private")) === "public"
                    ? <Globe className="h-3.5 w-3.5 text-[#C89B3C]" aria-label="Anyone with the link" />
                    : (e.visibility === "friends" ? <Users className="h-3.5 w-3.5 text-[#C89B3C]" aria-label="Friends only" /> : <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Private" />)}</span>
                </div>
              </div>
            </li>
          ))}
              </ul>
            </section>
          ))}
          <p className="pt-2 text-center text-sm text-muted-foreground">Curtain up on your next show 🎭 <Link to="/shows" className="text-[#C89B3C] underline">Browse shows</Link></p>
        </div>
      )}
    </div>
  );
}

/* ---------- page ---------- */
export default function MyTheatre() {
  const { user } = useAuth();
  const dialog = useDialog();
  const [data, setData] = useState(null);
  const [diary, setDiary] = useState(null);
  const [chatWith, setChatWith] = useState(() => {
    try { return (new URLSearchParams(window.location.search).get("chat") || "").toLowerCase().slice(0, 20) || null; } catch { return null; }
  });
  const [msgUnread, setMsgUnread] = useState(0);
  const [tab, setTab] = useState(() => {
    try {
      if (new URLSearchParams(window.location.search).get("chat")) return "messages";
      const t = localStorage.getItem(TAB_KEY);
      if (t === "notes") { localStorage.setItem("mytheatre-lists-sub", "notes"); return "lists"; }   // Lists and Notes are one tab now
      return ["feed", "messages", "diary", "lists", "friends"].includes(t) ? t : "watchlist"; } catch { return "watchlist"; }
  });
  const [alertsOff, setAlertsOff] = useState(false);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [trivia, setTrivia] = useState(null);
  const [form, setForm] = useState(null); // {show, entry}
  const [picking, setPicking] = useState(false);

  const loadDiary = useCallback(() => {
    api.get("/diary").then((r) => setDiary(r.data)).catch(() =>
      setDiary({ entries: [], stats: { shows: 0, this_year: 0, average_rating: null, theatres: 0, total: 0 }, needs_details: [], seat_areas: [], profile: { username: "", profile_public: false, blocked: false } }));
  }, []);
  const loadDash = useCallback(() => {
    api.get("/dashboard").then((r) => { setData(r.data); setAlertsOff(Boolean(r.data.email_alerts_off)); })
      .catch(() => setData({ followed_shows: [], followed_actors: [], activity: [], want_list: [], seen_list: [] }));
  }, []);

  useEffect(() => {
    if (!user) return;
    loadDash(); loadDiary();
    api.get("/trivia/me").then((r) => setTrivia(r.data)).catch(() => setTrivia(null));
  }, [user, loadDash, loadDiary]);

  useEffect(() => {
    if (!user) return undefined;
    const poll = () => api.get("/messages/unread").then((r) => setMsgUnread(r.data.unread || 0)).catch(() => {});
    poll();
    const t = setInterval(poll, 45000);
    window.addEventListener("focus", poll);
    return () => { clearInterval(t); window.removeEventListener("focus", poll); };
  }, [user]);
  const clearChatWith = useCallback(() => {
    setChatWith(null);
    try { if (window.location.search) window.history.replaceState(null, "", window.location.pathname); } catch { /* ignore */ }
  }, []);

  const chooseTab = (t) => { setTab(t); try { localStorage.setItem(TAB_KEY, t); } catch { /* ignore */ } };

  if (!user) return <SignInPrompt />;

  const wantList = data?.want_list || [];
  const followedShows = data?.followed_shows || [];
  const followedActors = data?.followed_actors || [];
  const quizResult = data?.quiz_result || null;
  const activity = data?.activity || [];
  const seatAreas = diary?.seat_areas || [];

  const afterSave = () => { setForm(null); loadDiary(); loadDash(); };

  const removeEntry = async (e) => {
    if (!(await dialog.confirm({ title: "Delete this diary entry?", body: `Your entry for ${e.show_name} will be deleted for good.`, confirmLabel: "Delete", danger: true }))) return;
    try { await api.delete(`/diary/${e.id}`); toast.success("Entry deleted"); loadDiary(); }
    catch { toast.error("Could not delete that. Please try again."); }
  };
  const suggestShow = async (e) => {
    try { await api.post(`/diary/${e.id}/suggest`); toast.success("Thanks! We'll look at adding it."); loadDiary(); }
    catch { toast.error("Could not send that. Please try again."); }
  };
  const removeQuiz = async () => {
    try { await api.delete("/quiz/save"); setData((d) => ({ ...d, quiz_result: null })); toast.success("Removed from your watchlist"); }
    catch { toast.error("Could not remove that. Please try again."); }
  };
  const toggleAlerts = async () => {
    const next = !alertsOff;
    setSavingAlerts(true);
    try { await api.post("/email-alerts", { off: next }); setAlertsOff(next); toast.success(next ? "Alert emails turned off" : "Alert emails turned on"); }
    catch { toast.error("Could not change that setting. Please try again."); }
    finally { setSavingAlerts(false); }
  };

  // On a phone the five tabs share the width, so the icons and the counts are hidden there (an unread-messages count stays).
  const tabBtn = (id, label, count, Icon, keepCount = false) => (
    <button type="button" role="tab" aria-selected={tab === id} onClick={() => chooseTab(id)} data-testid={`tab-${id}`}
      className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-full px-1.5 py-2.5 text-xs font-semibold transition-colors sm:flex-none sm:gap-2 sm:px-5 sm:text-sm ${tab === id ? "bg-[#C89B3C] text-black shadow" : "text-muted-foreground hover:text-foreground"}`}>
      <Icon className="hidden h-4 w-4 min-[430px]:block" />{label}{count > 0 && <span className={`${keepCount ? "" : "hidden sm:inline"} ${tab === id ? "text-black/70" : "text-muted-foreground"}`}>({count})</span>}
    </button>
  );

  return (
    <div>
      {dialog.node}
      <Seo title="My Theatre" noindex />
      <section className="relative overflow-hidden hero-scrim grain" style={{ "--hero-img": `url(${HERO})` }}>
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
          <p className="overline-label text-[#C89B3C] fade-up">My Theatre</p>
          <h1 className="mt-3 max-w-2xl font-serif text-4xl font-semibold leading-tight text-amber-50 sm:text-5xl fade-up">
            Your shows, your story.<span className="block text-[#E6C071]">Share it with friends.</span>
          </h1>
          <p className="mt-3 max-w-xl text-amber-100/70 fade-up">Keep track of what you want to see and everything you've seen. Post about a show, comment on your friends' posts, share lists and notes, and message the people you go with.</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {trivia && (
          <Link to="/trivia" className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm hover:border-[#C89B3C]/60" data-testid="trivia-strip">
            <span className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-[#C89B3C]" />
              {trivia.current_streak > 0 ? <>Trivia streak: <strong>{trivia.current_streak} day{trivia.current_streak > 1 ? "s" : ""}</strong></> : "Play today's Daily Theatre Trivia"}
            </span>
            <span className="text-muted-foreground">Play →</span>
          </Link>
        )}

        {diary?.profile && (
          <ProfileBar key={diary.profile.username || "new"} onFriends={() => chooseTab("friends")} friendsActive={tab === "friends"} profile={diary.profile} onProfile={(p) => setDiary((d) => ({ ...d, profile: p }))}
            suggest={(((user.name || user.first_name || "").trim().split(" ")[0] || "").toLowerCase().replace(/[^a-z0-9]/g, "")).slice(0, 20)} />
        )}

        {diary && <RequestsBanner />}

        <div className="mb-8">
          <div role="tablist" className="flex w-full gap-1 overflow-x-auto rounded-full border border-border bg-card p-1 sm:inline-flex sm:w-auto">
            {tabBtn("watchlist", "Watchlist", wantList.length, Bookmark)}
            {tabBtn("diary", "Diary", diary?.stats?.total || 0, BookOpen)}
            {tabBtn("feed", "Feed", 0, MessageCircle)}
            {tabBtn("messages", "Messages", msgUnread, Mail, true)}
            {tabBtn("lists", <><span className="sm:hidden">Lists</span><span className="hidden sm:inline">Lists &amp; notes</span></>, 0, ListOrdered)}
          </div>
        </div>

        {tab === "watchlist" && (!data ? <p className="text-muted-foreground">Loading…</p> : (
          <>
{quizResult && (
  <div data-testid="quiz-result" className="mb-8 rounded-2xl border border-[#C89B3C]/25 bg-card p-5">
    <p className="text-sm text-muted-foreground">Your West End role</p>
    <p className="font-serif text-2xl font-semibold text-foreground">{quizResult.role}</p>
    <p className="text-sm text-[#C89B3C]">from {quizResult.show}</p>
    {quizResult.line && <p className="mt-2 font-serif italic text-foreground/85">{quizResult.line}</p>}
    <div className="mt-4 flex flex-wrap items-center gap-3">
      {quizResult.slug && <Link to={`/shows/${quizResult.slug}`} className="rounded-full bg-[#C89B3C] px-5 py-2 text-sm font-semibold text-black">See the show</Link>}
      <Link to="/quiz" className="rounded-full border border-border px-5 py-2 text-sm font-medium text-foreground hover:bg-secondary">Retake the quiz</Link>
      <button type="button" onClick={removeQuiz} className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground"><Trash2 className="h-4 w-4" /> Remove</button>
    </div>
  </div>
)}
          <div className="grid gap-10 lg:grid-cols-3">
            <div className="space-y-12 lg:col-span-2">
              <section>
                <SectionTitle icon={Bookmark} tone="bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]" count={wantList.length}>Want to see</SectionTitle>
                <div className="mt-5">
                  {wantList.length === 0 ? (
                    <Hint>Nothing saved yet. Tap "Add to want to see" on any show to keep it here. <Link to="/shows" className="text-[#C89B3C] underline">Browse shows</Link></Hint>
                  ) : (
                    <div className="grid gap-6 sm:grid-cols-2">
                      {wantList.map((s, i) => (
                        <div key={s.id}>
                          <ShowCard show={s} index={i} />
                          <button type="button" data-testid={`seen-${s.id}`} onClick={() => setForm({ show: s })}
                            className="mt-2 w-full rounded-full border border-[#C89B3C]/60 py-2 text-sm font-semibold text-foreground hover:bg-[#C89B3C]/15">
                            <Check className="mr-1 inline h-4 w-4" /> Seen it? Add to diary
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              <section>
                <SectionTitle icon={Heart} tone="bg-primary/15 text-primary" count={followedShows.length}>Following</SectionTitle>
                <div className="mt-5">
                  {followedShows.length === 0 ? (
                    <Hint>You're not following any shows yet. Tap Follow on a show to get alerts when its cast changes.</Hint>
                  ) : (
                    <div className="grid gap-6 sm:grid-cols-2">{followedShows.map((s, i) => <ShowCard key={s.id} show={s} index={i} />)}</div>
                  )}
                </div>
                {followedActors.length > 0 && (
                  <div className="mt-10">
                    <h3 className="overline-label text-muted-foreground">Actors you follow</h3>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {followedActors.map((a) => (
                        <Link key={a.id} to={`/actors/${a.id}`} data-testid={`followed-actor-${a.id}`}
                          className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-primary">
                          <UserRound className="h-4 w-4 text-[hsl(var(--gold))]" />{a.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </section>

            </div>

            <div className="space-y-8">
              <div className="rounded-2xl border border-[#C89B3C]/25 bg-card p-5">
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#C89B3C]" />
                  <div>
                    <p className="font-medium text-foreground">Email alerts</p>
                    <p className="text-sm text-muted-foreground">{alertsOff ? "Off. You will not get emails about cast changes or booking." : "On. We email you about cast changes and booking for the shows and performers you follow."}</p>
                  </div>
                </div>
                <button type="button" onClick={toggleAlerts} disabled={savingAlerts}
                  className="mt-3 rounded-full border border-[#C89B3C]/70 px-5 py-1.5 text-sm font-semibold text-foreground hover:bg-[#C89B3C]/20 disabled:opacity-60">
                  {alertsOff ? "Turn on" : "Turn off"}
                </button>
              </div>

              <div>
                <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold"><Sparkles className="h-5 w-5 text-[hsl(var(--gold))]" /> Activity</h2>
                <div className="mt-5 space-y-3">
                  {activity.length === 0 && <Hint>No updates yet for anything on your list. When a cast change or booking update is logged, it'll appear here.</Hint>}
                  {activity.map((a) => (
                    <div key={a.id} data-testid={`activity-${a.id}`} className="rounded-xl border border-border bg-card p-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5"><ActivityIcon kind={a.kind} /></div>
                        <div className="flex-1">
                          <Link to={`/shows/${a.show_id}`} className="font-serif font-semibold hover:text-primary">{a.show_name}</Link>
                          <p className="text-sm text-foreground/80">{a.text}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{relFromNow(a.created_at) || fmtDate(a.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          </>
        ))}

        {tab === "lists" && <ListsNotesTab ShowPicker={ShowPicker} />}
        {tab === "messages" && <MessagesTab open={chatWith} onOpened={clearChatWith} onUnread={setMsgUnread} />}
        {tab === "feed" && <FeedTab ShowPicker={ShowPicker} onGoFriends={() => chooseTab("friends")} />}
        {tab === "friends" && <FriendsTab onGoDiary={() => chooseTab("diary")} onMessage={(u) => { setChatWith(u); chooseTab("messages"); }} />}

        {tab === "diary" && (!diary ? <p className="text-muted-foreground">Loading…</p> : (
          <DiaryTab diary={diary} onAdd={(s) => (s ? setForm({ show: s }) : setPicking(true))}
            onBulk={loadDiary} onSuggest={suggestShow} onAgain={(e) => setForm({ show: { id: e.show_id, name: e.show_name, venue: e.venue, custom: Boolean(e.is_custom) } })} onProfile={(p) => setDiary((d) => ({ ...d, profile: p }))} onEdit={(e) => setForm({ show: { id: e.show_id, name: e.show_name, venue: e.venue, custom: Boolean(e.is_custom) }, entry: e })} onDelete={removeEntry} />
        ))}
      </div>

      {picking && <ShowPicker onClose={() => setPicking(false)} onPick={(s) => { setPicking(false); setForm({ show: s }); }} />}
      {form && <EntryForm show={form.show} entry={form.entry} seatAreas={seatAreas} profile={diary?.profile} onClose={() => setForm(null)} onSaved={afterSave} />}
    </div>
  );
}